import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { 
  Package, Search, Users, Send, Phone, ClipboardCheck, 
  FileText, Handshake, ChevronRight, ChevronLeft,
  Sparkles, CheckCircle2, Briefcase, X, SkipForward,
  Play, ArrowRight, GripVertical, Minus, Maximize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useWizardProgress } from "@/hooks/useWizardProgress";

type Position = 'top' | 'bottom' | 'left' | 'right';

const WIZARD_STEPS = [
  {
    id: 1,
    title: 'Select Your Product',
    description: 'Choose the product you want to sell',
    icon: Package,
    route: '/app/products',
    selector: '[data-wizard="products"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Click on any product card to select it for your sales cycle. This helps AI personalize everything.',
      intermediate: 'Product selection enables AI-powered lead matching and proposal generation.',
      advanced: 'Product context initializes the AI pipeline for targeted discovery and pricing.'
    }
  },
  {
    id: 2,
    title: 'Discover Leads with AI',
    description: 'Let AI find potential customers',
    icon: Search,
    route: '/app/leads',
    selector: '[data-wizard="ai-discovery"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Click the "AI Discovery" button to let AI automatically find leads for your product.',
      intermediate: 'Configure industry and keywords for targeted lead harvesting from web sources.',
      advanced: 'Multi-source discovery: web scraping, LinkedIn, directories with ICP scoring.'
    }
  },
  {
    id: 3,
    title: 'Review & Score Leads',
    description: 'Prioritize leads by AI score',
    icon: Users,
    route: '/app/leads',
    selector: '[data-wizard="leads-table"]',
    fallbackSelector: 'main',
    position: 'top' as Position,
    tips: {
      beginner: 'Leads with higher scores (green badges) are more likely to convert. Focus on these first!',
      intermediate: 'AI scores combine engagement signals, firmographic data, and ICP fit.',
      advanced: 'Lead scoring updates in real-time based on all interaction history.'
    }
  },
  {
    id: 4,
    title: 'Setup Outreach Sequences',
    description: 'Create multi-channel campaigns',
    icon: Send,
    route: '/app/outreach',
    selector: '[data-wizard="sequences"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Pick a sequence template and AI will personalize messages for each lead automatically.',
      intermediate: 'Multi-channel sequences (email + WhatsApp + SMS) increase response rates by 3x.',
      advanced: 'Configure delay timings, A/B test subject lines, and optimize send times.'
    }
  },
  {
    id: 5,
    title: 'Make Sales Calls',
    description: 'Connect with AI assistance',
    icon: Phone,
    route: '/app/calls',
    selector: '[data-wizard="calls"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Click "Schedule Call" to set up a call. AI will guide you during the conversation with tips!',
      intermediate: 'AI transcribes calls in real-time and detects objections for suggested responses.',
      advanced: 'Review call analytics and sentiment trends to continuously improve your pitch.'
    }
  },
  {
    id: 6,
    title: 'Qualify Leads',
    description: 'Apply BANT/MEDDIC frameworks',
    icon: ClipboardCheck,
    route: '/app/leads',
    selector: '[data-wizard="qualification"]',
    fallbackSelector: 'main',
    position: 'right' as Position,
    tips: {
      beginner: 'Answer simple questions to determine if this lead is ready to buy.',
      intermediate: 'Use BANT (Budget, Authority, Need, Timeline) for structured qualification.',
      advanced: 'Qualification scores feed directly into deal win probability calculations.'
    }
  },
  {
    id: 7,
    title: 'Create Deal',
    description: 'Convert leads to opportunities',
    icon: Briefcase,
    route: '/app/deals',
    selector: '[data-wizard="create-deal"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Click "Add Deal" to create a new sales opportunity from your qualified lead.',
      intermediate: 'AI suggests optimal pricing based on account size and historical win rates.',
      advanced: 'Configure pipeline stages and AI forecasting for your sales process.'
    }
  },
  {
    id: 8,
    title: 'Generate Proposal',
    description: 'AI creates professional proposals',
    icon: FileText,
    route: '/app/proposals',
    selector: '[data-wizard="proposals"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Click "Create Proposal" and AI generates a complete professional document in seconds!',
      intermediate: 'AI proposals include executive summary, solution details, and ROI calculations.',
      advanced: 'Customize templates, add merge fields, and include case studies.'
    }
  },
  {
    id: 9,
    title: 'Close & Contract',
    description: 'Finalize with e-signatures',
    icon: Handshake,
    route: '/app/contracts',
    selector: '[data-wizard="contracts"]',
    fallbackSelector: 'main',
    position: 'bottom' as Position,
    tips: {
      beginner: 'Send the contract for signature. Your customer can sign electronically from anywhere!',
      intermediate: 'Track contract views, reminder emails, and signature status in real-time.',
      advanced: 'Configure multi-level approval workflows and version control.'
    }
  }
];

interface SpotlightOverlayProps {
  targetRect: DOMRect | null;
  step: typeof WIZARD_STEPS[0];
  currentStep: number;
  totalSteps: number;
  completedSteps: number[];
  guidanceLevel: string;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onClose: () => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
  savedPosition: { x: number; y: number } | null;
  onSavePosition: (pos: { x: number; y: number }) => void;
}

// Haptic feedback utility
const triggerHaptic = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if ('vibrate' in navigator) {
    const patterns = { light: 10, medium: 20, heavy: 40 };
    navigator.vibrate(patterns[type]);
  }
};

function SpotlightOverlay({
  targetRect,
  step,
  currentStep,
  totalSteps,
  completedSteps,
  guidanceLevel,
  onNext,
  onPrev,
  onSkip,
  onClose,
  isMinimized,
  onToggleMinimize,
  savedPosition,
  onSavePosition
}: SpotlightOverlayProps) {
  const Icon = step.icon;
  const tip = step.tips[guidanceLevel as keyof typeof step.tips] || step.tips.beginner;
  
  // Draggable state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(savedPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate initial tooltip position
  const getInitialPosition = useCallback(() => {
    if (!targetRect) {
      return { x: window.innerWidth / 2 - 175, y: window.innerHeight / 2 - 140 };
    }

    const padding = 16;
    const tooltipWidth = 350;
    const tooltipHeight = 260;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const clampX = (x: number) => Math.max(padding, Math.min(x, viewportWidth - tooltipWidth - padding));
    const clampY = (y: number) => Math.max(padding, Math.min(y, viewportHeight - tooltipHeight - padding));

    let x: number;
    let y: number;

    switch (step.position) {
      case 'bottom':
        y = targetRect.bottom + padding;
        if (y + tooltipHeight > viewportHeight - padding) {
          y = Math.max(padding, targetRect.top - tooltipHeight - padding);
        }
        x = clampX(targetRect.left + targetRect.width / 2 - tooltipWidth / 2);
        break;
      case 'top':
        y = targetRect.top - tooltipHeight - padding;
        if (y < padding) {
          y = Math.min(viewportHeight - tooltipHeight - padding, targetRect.bottom + padding);
        }
        x = clampX(targetRect.left + targetRect.width / 2 - tooltipWidth / 2);
        break;
      case 'right':
        y = clampY(targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
        x = targetRect.right + padding;
        if (x + tooltipWidth > viewportWidth - padding) {
          x = Math.max(padding, targetRect.left - tooltipWidth - padding);
        }
        break;
      case 'left':
        y = clampY(targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
        x = targetRect.left - tooltipWidth - padding;
        if (x < padding) {
          x = Math.min(viewportWidth - tooltipWidth - padding, targetRect.right + padding);
        }
        break;
      default:
        y = clampY(targetRect.bottom + padding);
        x = clampX(targetRect.left);
    }

    return { x, y };
  }, [targetRect, step.position]);

  // Set initial position when step changes (only if no saved position)
  useEffect(() => {
    if (!savedPosition) {
      setPosition(getInitialPosition());
    }
  }, [currentStep, getInitialPosition, savedPosition]);

  // Handle drag start (mouse)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!position) return;
    e.preventDefault();
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: position.x,
      initialY: position.y
    };
  };

  // Handle drag start (touch)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!position) return;
    const touch = e.touches[0];
    triggerHaptic('light');
    setIsDragging(true);
    dragRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      initialX: position.x,
      initialY: position.y
    };
  };

  // Handle drag move (mouse & touch)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      
      const newX = Math.max(8, Math.min(dragRef.current.initialX + dx, window.innerWidth - 358));
      const newY = Math.max(8, Math.min(dragRef.current.initialY + dy, window.innerHeight - 268));
      
      setPosition({ x: newX, y: newY });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!dragRef.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragRef.current.startX;
      const dy = touch.clientY - dragRef.current.startY;
      
      const newX = Math.max(8, Math.min(dragRef.current.initialX + dx, window.innerWidth - 358));
      const newY = Math.max(8, Math.min(dragRef.current.initialY + dy, window.innerHeight - 268));
      
      setPosition({ x: newX, y: newY });
    };

    const handleEnd = () => {
      setIsDragging(false);
      // Save position when drag ends
      if (dragRef.current && position) {
        onSavePosition(position);
      }
      dragRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    window.addEventListener('touchcancel', handleEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging]);

  if (!position) return null;

  return createPortal(
    <>
      {/* Non-blocking highlight ring around target - pointer-events-none allows clicks through */}
      {targetRect && (
        <div
          className="fixed border-2 border-primary rounded-xl pointer-events-none animate-pulse z-[9998]"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 4px hsl(var(--primary) / 0.3), 0 0 30px hsl(var(--primary) / 0.4)'
          }}
        />
      )}

      {/* Draggable Tooltip Card - does NOT block the rest of the page */}
      <div
        ref={tooltipRef}
        className={cn(
          "fixed w-[350px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-[9999]",
          isDragging ? "cursor-grabbing" : ""
        )}
        style={{ top: position.y, left: position.x }}
      >
        {/* Drag Handle Header */}
        <div 
          className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-3 border-b border-border cursor-grab select-none touch-none"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <div className="p-1.5 bg-primary/20 rounded-lg">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              </div>
              <span className="font-semibold text-sm">Sales Wizard</span>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          
          {/* Step indicators */}
          <div className="flex items-center gap-1">
            {WIZARD_STEPS.map((s) => (
              <div
                key={s.id}
                className={cn(
                  "h-1 flex-1 rounded-full transition-all",
                  completedSteps.includes(s.id) ? "bg-primary" :
                  s.id === currentStep ? "bg-primary/60" : "bg-muted"
                )}
              />
            ))}
          </div>
        </div>

        {/* Content - only show when not minimized */}
        {!isMinimized && (
          <div className="p-3">
            <div className="flex items-start gap-2 mb-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 shrink-0">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium text-primary">Step {currentStep}/{totalSteps}</span>
                </div>
                <h3 className="font-semibold text-sm">{step.title}</h3>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>
            </div>

            {/* Tip */}
            <div className="bg-muted/50 rounded-lg p-2 mb-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground leading-relaxed">{tip}</p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onPrev}
                disabled={currentStep === 1}
                className="gap-1 h-7 text-xs px-2"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={onSkip}
                className="text-muted-foreground gap-1 h-7 text-xs px-2"
              >
                <SkipForward className="h-3.5 w-3.5" />
                Skip
              </Button>

              <Button
                size="sm"
                onClick={onNext}
                className="gap-1 h-7 text-xs px-2"
              >
                {currentStep === totalSteps ? (
                  <>
                    Finish
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}

export function FloatingSalesWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    progress,
    isLoading,
    steps,
    currentStepInfo,
    completionPercentage,
    goToStep,
    completeStep,
    dismissWizard,
    showWizard,
    guidanceLevel,
  } = useWizardProgress();

  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [savedPosition, setSavedPosition] = useState<{ x: number; y: number } | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const currentStep = progress?.current_step || 1;
  const completedSteps = progress?.completed_steps || [];
  const currentWizardStep = WIZARD_STEPS.find(s => s.id === currentStep) || WIZARD_STEPS[0];

  // Find and track the target element
  const updateTargetRect = useCallback(() => {
    if (!isOverlayActive || !currentWizardStep) return;

    const element = document.querySelector(currentWizardStep.selector) || 
                   document.querySelector(currentWizardStep.fallbackSelector);
    
    if (element) {
      setTargetRect(element.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [isOverlayActive, currentWizardStep]);

  // Update target rect on scroll/resize
  useEffect(() => {
    if (!isOverlayActive) return;

    updateTargetRect();
    
    window.addEventListener('scroll', updateTargetRect, true);
    window.addEventListener('resize', updateTargetRect);

    // Also observe DOM changes
    observerRef.current = new MutationObserver(updateTargetRect);
    observerRef.current.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', updateTargetRect, true);
      window.removeEventListener('resize', updateTargetRect);
      observerRef.current?.disconnect();
    };
  }, [isOverlayActive, updateTargetRect]);

  // Navigate to step route when overlay activates
  useEffect(() => {
    if (isOverlayActive && currentWizardStep && location.pathname !== currentWizardStep.route) {
      navigate(currentWizardStep.route);
    }
  }, [isOverlayActive, currentWizardStep, location.pathname, navigate]);

  // Update rect after navigation
  useEffect(() => {
    if (isOverlayActive) {
      const timer = setTimeout(updateTargetRect, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isOverlayActive, updateTargetRect]);

  const handleStartTour = () => {
    setIsOverlayActive(true);
  };

  const handleNext = async () => {
    await completeStep(currentStep);
    if (currentStep < WIZARD_STEPS.length) {
      await goToStep(currentStep + 1);
      const nextStep = WIZARD_STEPS.find(s => s.id === currentStep + 1);
      if (nextStep) {
        navigate(nextStep.route);
      }
    } else {
      setIsOverlayActive(false);
      await dismissWizard();
    }
  };

  const handlePrev = async () => {
    if (currentStep > 1) {
      await goToStep(currentStep - 1);
      const prevStep = WIZARD_STEPS.find(s => s.id === currentStep - 1);
      if (prevStep) {
        navigate(prevStep.route);
      }
    }
  };

  const handleSkip = async () => {
    setIsOverlayActive(false);
    await dismissWizard();
  };

  const handleClose = () => {
    setIsOverlayActive(false);
  };

  if (isLoading || !progress) return null;

  // Show resume button if dismissed
  if (progress.is_dismissed && !isOverlayActive) {
    return (
      <Button
        variant="default"
        size="sm"
        className="fixed bottom-24 right-6 z-50 shadow-lg gap-2 ai-glow"
        onClick={() => {
          showWizard();
          setIsOverlayActive(true);
        }}
      >
        <Sparkles className="h-4 w-4" />
        Resume Tour
      </Button>
    );
  }

  // Show overlay when active
  if (isOverlayActive) {
    return (
      <SpotlightOverlay
        targetRect={targetRect}
        step={currentWizardStep}
        currentStep={currentStep}
        totalSteps={WIZARD_STEPS.length}
        completedSteps={completedSteps}
        guidanceLevel={guidanceLevel}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onClose={handleClose}
        isMinimized={isMinimized}
        onToggleMinimize={() => setIsMinimized(!isMinimized)}
        savedPosition={savedPosition}
        onSavePosition={setSavedPosition}
      />
    );
  }

  // Show floating start button
  const StepIcon = currentWizardStep.icon;

  return (
    <div className="fixed bottom-24 right-6 z-50">
      <div className="bg-card border border-border rounded-xl shadow-xl p-4 w-72 animate-scale-in">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <span className="font-semibold text-sm">Sales Wizard</span>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={dismissWizard}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <Progress value={completionPercentage} className="h-1.5 mb-3" />

        <div className="flex items-center gap-3 mb-3 p-2 bg-muted/50 rounded-lg">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <StepIcon className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Step {currentStep}/9</p>
            <p className="text-sm font-medium truncate">{currentWizardStep.title}</p>
          </div>
        </div>

        <Button 
          className="w-full gap-2" 
          onClick={handleStartTour}
        >
          <Play className="h-4 w-4" />
          Start Guided Tour
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
